// ============================================
// switchViewport.js
// 小さい画面幅での viewport 固定制御
// ============================================

import { rafThrottle } from '../utils/rafThrottle';

// ============================================
// 設定値
// ============================================
const CONFIG = {
  breakpoint: 375,
  fixedWidth: 375,
};

const VIEWPORT_RESPONSIVE = 'width=device-width,initial-scale=1';

// ============================================
// viewport 切り替え処理
// ============================================
function viewport() {
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (!viewportMeta) return;

  const root = document.documentElement;
  const onResize = rafThrottle(applyViewport);

  applyViewport();

  window.addEventListener('resize', onResize, {
    passive: true,
  });

  window.addEventListener('orientationchange', onResize);

  // ============================================
  // 実際の画面幅を取得
  // ============================================
  function getScreenWidth() {
    const visualViewport = window.visualViewport;

    /**
     * width=375 に変更した後は window.innerWidth が
     * 375px になるため、その値だけでは実端末幅を取得できない。
     *
     * visualViewport.width × scale で、
     * viewport 設定変更後も実際の表示領域幅を取得する。
     *
     * 例:
     * 320px端末
     * visualViewport.width = 375
     * scale = 0.853333...
     *
     * 375 × 0.853333... ≒ 320
     */
    if (visualViewport?.width && visualViewport?.scale) {
      return visualViewport.width * visualViewport.scale;
    }

    /**
     * VisualViewport API が使えないブラウザ向け。
     */
    if (window.outerWidth > 0) {
      return window.outerWidth;
    }

    if (window.screen?.width > 0) {
      return window.screen.width;
    }

    return window.innerWidth || document.documentElement.clientWidth || 0;
  }

  // ============================================
  // 375pxレイアウトを画面内に収めるscale
  // ============================================
  function getFixedScale(screenWidth) {
    const scale = screenWidth / CONFIG.fixedWidth;

    return Math.max(0.1, Math.min(1, scale));
  }

  // ============================================
  // viewport content生成
  // ============================================
  function getViewportContent(screenWidth) {
    /**
     * 375px以上
     *
     * 通常のレスポンシブ表示。
     */
    if (screenWidth >= CONFIG.breakpoint) {
      return VIEWPORT_RESPONSIVE;
    }

    /**
     * 375px未満
     *
     * レイアウト自体は375pxで維持したまま、
     * 端末幅に合わせて縮小する。
     *
     * 320px → 320 / 375 = 0.853333...
     * 360px → 360 / 375 = 0.96
     */
    const scale = getFixedScale(screenWidth);
    const initialScale = Number(scale.toFixed(6));

    return `width=${CONFIG.fixedWidth},initial-scale=${initialScale}`;
  }

  // ============================================
  // viewport meta が無視される環境向け
  // ============================================
  function applyCssFallback(shouldFix, screenWidth) {
    const layoutWidth = root.clientWidth || window.innerWidth || 0;
    const useFallback = shouldFix && layoutWidth < CONFIG.fixedWidth;
    const nextZoom = useFallback ? String(screenWidth / CONFIG.fixedWidth) : '';
    const nextMinWidth = useFallback ? `${CONFIG.fixedWidth}px` : '';

    if (root.style.zoom !== nextZoom) {
      root.style.zoom = nextZoom;
    }
    if (root.style.minWidth !== nextMinWidth) {
      root.style.minWidth = nextMinWidth;
    }
  }

  // ============================================
  // viewport反映
  // ============================================
  function applyViewport() {
    const screenWidth = getScreenWidth();

    if (!screenWidth) return;

    const newContent = getViewportContent(screenWidth);
    const shouldFix = newContent !== VIEWPORT_RESPONSIVE;

    if (viewportMeta.getAttribute('content') !== newContent) {
      viewportMeta.setAttribute('content', newContent);
    }

    applyCssFallback(shouldFix, screenWidth);
  }
}

export { viewport };
